import { CommonModule } from "@angular/common";
import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { NgGlyph } from "@ng-icons/core";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { AppSummaryPanelComponent, SummaryPanelRow } from "../../../../Common/Components/SummaryPanel/SummaryPanelComponent";
import { formatDateNumeric } from "../../../../Common/Utility/UtilityDate";
import { WarehouseApi } from "../../../Warehouse/Api/WarehouseApi";
import { Warehouse } from "../../../Warehouse/Dto/WarehouseModel";
import { StockEffects } from "../../State/StockEffects";
import { StockState } from "../../State/StockState";

@Component({
  selector: "app-stock-home",
  standalone: true,
  imports: [CommonModule, FormsModule, NgGlyph, AppButtonComponent, AppCardComponent, AppSummaryPanelComponent],
  templateUrl: "./StockHomePage.html",
  styleUrl: "./StockHomePage.scss",
})
export class StockHomePage implements OnInit, OnDestroy {
  private readonly stockEffects = inject(StockEffects);
  readonly stockState = inject(StockState);
  private readonly warehouseApi = inject(WarehouseApi);
  private readonly router = inject(Router);

  warehouses: Warehouse[] = [];
  searchTerm = "";
  readonly pageSizeOptions = [6, 12, 24];
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;
  readonly expandedMaterialIds = new Set<string>();

  async ngOnInit(): Promise<void> {
    const searchParams = this.stockState.searchParams();
    this.searchTerm = searchParams.search ?? "";

    await Promise.all([this.loadSummary(), this.loadWarehouses()]);
  }

  ngOnDestroy(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  }

  get selectedWarehouseId(): string {
    return this.stockState.searchParams().warehouseId ?? "";
  }

  get selectedWarehouseName(): string {
    if (!this.selectedWarehouseId) {
      return "All Warehouses";
    }

    return this.warehouses.find((warehouse) => warehouse.id === this.selectedWarehouseId)?.name ?? "Selected Warehouse";
  }

  get summaryRows(): SummaryPanelRow[] {
    return [
      { label: "Scope", value: this.selectedWarehouseName },
      { label: "Total On Hand", value: this.formatQuantity(this.stockState.totals().totalOnHand) },
      { label: "Total Reserved", value: this.formatQuantity(this.stockState.totals().totalReserved) },
      { label: "Total Available", value: this.formatQuantity(this.stockState.totals().totalAvailable) },
    ];
  }

  async onWarehouseChange(value: string): Promise<void> {
    await this.stockEffects.searchStockSummary({
      ...this.stockState.searchParams(),
      page: 1,
      warehouseId: value || undefined,
    });
    this.expandedMaterialIds.clear();
  }

  onSearchInput(value: string): void {
    this.searchTerm = value;

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    this.searchTimeout = setTimeout(() => {
      this.stockEffects.searchStockSummary({
        ...this.stockState.searchParams(),
        page: 1,
        search: this.searchTerm.trim() || undefined,
      });
      this.expandedMaterialIds.clear();
    }, 300);
  }

  async onPageChange(page: number): Promise<void> {
    const pagination = this.stockState.pagination();
    if (page < 1 || page > pagination.pages || page === pagination.page) {
      return;
    }

    await this.stockEffects.searchStockSummary({ ...this.stockState.searchParams(), page });
  }

  async onPageSizeChange(value: string): Promise<void> {
    const limit = Number(value);
    if (!Number.isFinite(limit) || limit === this.stockState.pagination().limit) {
      return;
    }

    await this.stockEffects.searchStockSummary({
      ...this.stockState.searchParams(),
      page: 1,
      limit,
    });
    this.expandedMaterialIds.clear();
  }

  toggleExpanded(materialId: string): void {
    if (this.expandedMaterialIds.has(materialId)) {
      this.expandedMaterialIds.delete(materialId);
      return;
    }

    this.expandedMaterialIds.add(materialId);
  }

  isExpanded(materialId: string): boolean {
    return this.expandedMaterialIds.has(materialId);
  }

  getPaginationRange(): number[] {
    const pagination = this.stockState.pagination();
    const currentPage = pagination.page;
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(pagination.pages, currentPage + 2);

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }

  getPaginationStart(): number {
    const pagination = this.stockState.pagination();
    if (pagination.total === 0) {
      return 0;
    }

    return (pagination.page - 1) * pagination.limit + 1;
  }

  getPaginationEnd(): number {
    const pagination = this.stockState.pagination();
    return Math.min(pagination.page * pagination.limit, pagination.total);
  }

  goToAdjustment(): void {
    this.router.navigate(["/stocks/adjustments/create"]).catch(() => undefined);
  }

  openStockDetail(stockId: string): void {
    if (!stockId) {
      return;
    }

    this.router.navigate(["/stocks", stockId]).catch(() => undefined);
  }

  formatQuantity(value: number): string {
    return new Intl.NumberFormat("en-US").format(value);
  }

  formatUpdatedAt(value?: string | Date): string {
    return (() => {
      if (value) {
        return formatDateNumeric(value);
      }
      return "-";
    })();
  }

  getAvailabilityTone(available: number): "healthy" | "warning" | "critical" {
    if (available <= 0) {
      return "critical";
    }

    if (available <= 25) {
      return "warning";
    }

    return "healthy";
  }

  private async loadSummary(): Promise<void> {
    await this.stockEffects.searchStockSummary(this.stockState.searchParams());
  }

  private async loadWarehouses(): Promise<void> {
    try {
      const response = await this.warehouseApi.searchWarehouses({ page: 1, limit: 100 });
      this.warehouses = response.data.sort((left, right) => left.name.localeCompare(right.name));
    } catch {
      this.warehouses = [];
    }
  }
}
